import { MobileLayout } from 'components/mobile/MobileLayout/MobileLayout';
import { useModernDesktop } from 'components/Layout/ModernDesktopShell';
import { PrematchCatalogContext } from 'context/prematchCatalogContext';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import {
  getHighlights,
  getLastMinuteEvents,
  getPopularSlips,
} from 'services/api/sportBetsService';
import { MatchOffer } from 'types/MatchOffer';
import useAsyncEffect from 'use-async-effect';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import styles from '../styles/pages/HighLightsAndLastMinute.module.css';
import { Loader } from 'components/Loader/Loader';
import { MatchMobile } from 'components/mobile/MatchMobile/MatchMobile';
import { useBreakpoint } from 'hooks/useBreakpoint';

export const HighLightsAndLastMinute = () => {
  const { lastPrematchPush } = useContext(PrematchCatalogContext);
  const prematchCatalogBump = lastPrematchPush?.version ?? 0;
  const [matches, setMatches] = useState<MatchOffer[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const modernDesktop = useModernDesktop();
  const page = useLocation().pathname.split('/').pop();

  useAsyncEffect(async () => {
    setIsLoading(true);
    setMatches(
      page === 'highlights'
        ? await getHighlights()
        : page === 'most-played-events'
          ? await getMostPlayedMatches()
          : await getLastMinuteEvents()
    );
    setIsLoading(false);
  }, [page, prematchCatalogBump]);

  if (!isMobile && !modernDesktop) {
    return <Navigate to="/not-found" />;
  }

  const isHighlights = page === 'highlights';
  const kind = isHighlights
    ? 'highlights'
    : page === 'most-played-events'
      ? 'most-played-events'
      : 'last-minute-events';
  const title = t(
    isHighlights
      ? 'highlights_label'
      : kind === 'most-played-events'
        ? 'most_played_events_label'
        : 'last_minute_events_label'
  );

  return (
    <MobileLayout pageTitle={title}>
      <div className={styles.header}>
        <ThemeIcon
          icon={kind}
          alt={kind}
          variant="inline"
          tone="chrome"
          className={styles.icon}
        />
        <span>{title}</span>
      </div>
      <div className={styles.matches}>
        {isLoading && <Loader />}
        {!isLoading &&
          matches &&
          matches.length > 0 &&
          matches.map((m) => (
            <MatchMobile key={m.fi} match={m} isMultiLeagueList />
          ))}
        {!isLoading && matches && matches.length === 0 && (
          <p className={styles.empty}>{t('no_events_in_period')}</p>
        )}
      </div>
    </MobileLayout>
  );
};

/**
 * "Most played events": the matches of the most played slips (the modern
 * home's rail), each once, the most played first, as matches with their
 * odds rather than slips. Only matches still to start.
 */
async function getMostPlayedMatches(): Promise<MatchOffer[]> {
  const data = await getPopularSlips();
  if (!data) return [];
  const played = new Map<number, number>();
  data.slips.forEach((s) =>
    s.legs.forEach((l) =>
      played.set(l.fi, (played.get(l.fi) ?? 0) + (s.played || 1))
    )
  );
  const now = Date.now();
  return data.events
    .filter((m) => played.has(m.fi) && m.time * 1000 > now)
    .sort(
      (a, b) =>
        (played.get(b.fi) ?? 0) - (played.get(a.fi) ?? 0) || a.time - b.time
    );
}
